<template>
  <!-- ===== 站内消息铃铛 ===== -->
  <div
    class="notice-dropdown"
    @mouseenter="openNoticeDropdown"
    @mouseleave="closeNoticeDropdown"
  >
    <div class="notice-trigger" :title="$t('notice.title')">
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
      </svg>
      <span v-if="unreadCount > 0" class="notice-dot"></span>
    </div>

    <!-- 消息下拉面板 -->
    <transition name="fade">
      <div v-show="isNoticeDropdownOpen" class="notice-panel">
        <div class="notice-panel-header">
          <span class="notice-panel-title">{{ $t("notice.title") }}</span>
          <span v-if="unreadCount > 0" class="notice-panel-unread">
            {{ unreadCount }} {{ $t("notice.unread") }}
          </span>
        </div>

        <div v-if="loading && !list.length" class="notice-empty">
          <span class="notice-empty-icon">🔔</span>
          <span>{{ $t("notice.loading") }}</span>
        </div>
        <div v-else-if="!list.length" class="notice-empty">
          <span class="notice-empty-icon">🔔</span>
          <span>{{ $t("notice.empty") }}</span>
        </div>
        <div v-else class="notice-list">
          <div
            v-for="item in list"
            :key="item.id"
            class="notice-item"
            :class="{ unread: item.is_read !== 2 }"
            @click="handleNoticeClick(item)"
          >
            <span v-if="item.is_read !== 2" class="notice-item-dot"></span>
            <div class="notice-item-content">
              <div class="notice-item-title">{{ item.title }}</div>
              <div class="notice-item-preview">{{ preview(item.content) }}</div>
              <div class="notice-item-time">{{ formatTime(item) }}</div>
            </div>
          </div>
        </div>

        <div v-if="list.length" class="notice-panel-footer">
          <button
            class="notice-more-btn"
            :disabled="loading || !hasMore"
            @click="loadMore"
          >
            {{
              loading
                ? $t("notice.loading")
                : hasMore
                ? $t("notice.loadMore")
                : $t("notice.noMore")
            }}
          </button>
        </div>
      </div>
    </transition>

    <!-- 消息详情遮罩弹框 -->
    <el-dialog
      :visible.sync="detailVisible"
      :z-index="4000"
      width="760px"
      top="7vh"
      class="notice-dialog"
      append-to-body
      @closed="onDetailClosed"
    >
      <div slot="title" class="notice-dialog-header">
        <span class="notice-dialog-icon">
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
        </span>
        <div class="notice-dialog-heading">
          <span class="notice-dialog-kicker">{{ $t("notice.title") }}</span>
          <h3 class="notice-dialog-headline">
            {{ activeNotice ? activeNotice.title : $t("notice.detail") }}
          </h3>
        </div>
      </div>

      <div v-if="activeNotice" class="notice-detail">
        <div class="notice-detail-meta">
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="9"></circle>
            <path d="M12 7v5l3 2"></path>
          </svg>
          <span>{{ formatTime(activeNotice) }}</span>
        </div>
        <!-- 正文由后台富文本编辑器维护，按 HTML 渲染（字体/颜色/表格/单元格底色等） -->
        <div
          class="notice-detail-content notice-rich"
          v-html="detailHtml"
        ></div>
      </div>

      <div slot="footer" class="notice-dialog-footer">
        <div class="notice-signature">
          <img
            src="../assets/logo.png"
            alt="aetrivol"
            class="notice-signature-logo"
          />
          <span class="notice-signature-name">aetrivol</span>
        </div>
        <el-button
          type="primary"
          class="notice-close-btn"
          @click="detailVisible = false"
        >
          {{ $t("notice.close") }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 预购口令弹框 -->
    <el-dialog
      :visible.sync="activityPasswordVisible"
      width="420px"
      class="activity-password-dialog"
      append-to-body
      :close-on-click-modal="false"
    >
      <div slot="title" class="activity-dialog-title">
        🔐 {{ $t("activity.passwordTitle") }}
      </div>
      <div class="activity-password-body">
        <p class="activity-password-tip">
          {{ $t("activity.passwordTip") }}
        </p>
        <el-input
          v-model="activityPassword"
          maxlength="10"
          show-word-limit
          :placeholder="$t('activity.passwordPlaceholder')"
          @keyup.enter.native="verifyActivityPassword"
        />
        <div v-if="activityPasswordError" class="activity-password-error">
          {{ activityPasswordError }}
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="activityPasswordVisible = false">
          {{ $t("activity.cancel") }}
        </el-button>
        <el-button
          type="primary"
          :loading="activityPasswordChecking"
          @click="verifyActivityPassword"
        >
          {{ $t("activity.confirm") }}
        </el-button>
      </span>
    </el-dialog>

    <!-- 服务器租赁预购弹框（与普通消息详情共用弹框样式） -->
    <el-dialog
      :visible.sync="preorderVisible"
      :z-index="4000"
      width="760px"
      top="7vh"
      class="notice-dialog activity-preorder-dialog"
      append-to-body
      :close-on-click-modal="false"
    >
      <div slot="title" class="notice-dialog-header">
        <span class="notice-dialog-icon">
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
        </span>
        <div class="notice-dialog-heading">
          <span class="notice-dialog-kicker">{{ $t("notice.title") }}</span>
          <h3 class="notice-dialog-headline">
            {{
              activityNotice
                ? activityNotice.title
                : $t("activity.preorderTitle")
            }}
          </h3>
        </div>
      </div>

      <div v-if="activityItem" class="notice-detail activity-preorder">
        <div v-if="activityNotice" class="notice-detail-meta">
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="9"></circle>
            <path d="M12 7v5l3 2"></path>
          </svg>
          <span>{{ formatTime(activityNotice) }}</span>
        </div>

        <div class="ap-body">
          <div
            v-if="activityTopHtml"
            class="ap-copy ap-copy-top notice-rich"
            v-html="activityTopHtml"
          ></div>

          <div class="ap-panel">
            <!--<div class="ap-discounts" v-if="preorderDiscounts.length">
            <div class="ap-section-title">{{ $t("activity.discountTitle") }}</div>
            <div class="ap-discount-list">
              <div
                v-for="(rule, index) in preorderDiscounts"
                :key="index"
                class="ap-discount-item"
                :class="{ active: preorderActiveDiscountIndex === index }"
              >
                <span class="ap-discount-text">
                  {{ $t("activity.discountRule", {
                    amount: Number(rule.condition).toFixed(2),
                    percent: (Number(rule.discount) * 100).toFixed(2),
                  }) }}
                </span>
                <el-tag
                  v-if="preorderActiveDiscountIndex === index"
                  size="mini"
                  type="success"
                  effect="dark"
                >
                  {{ $t("activity.discountHit") }}
                </el-tag>
              </div>
            </div>
          </div>
          -->

            <div class="ap-form">
              <div class="ap-share-head">
                <div class="ap-section-title">
                  {{ $t("activity.selectShare") }}
                </div>
                <span class="ap-share-remaining">
                  {{ $t("activity.remainingShare") }}:
                  {{ formatShare(preorderRemaining) }}%
                </span>
              </div>
              <div class="ap-share-row">
                <div class="ap-share-slider-wrap">
                  <el-slider
                    v-model="preorderShare"
                    class="ap-share-slider"
                    :min="preorderMinShare"
                    :max="preorderRemaining"
                    :step="1"
                    :show-tooltip="false"
                    :disabled="preorderRemaining <= 0"
                  />
                  <div class="ap-slider-labels">
                    <span>{{ formatShare(preorderMinShare) }}%</span>
                    <span class="ap-slider-current">
                      {{ formatShare(preorderShare) }}%
                    </span>
                    <span>{{ formatShare(preorderRemaining) }}%</span>
                  </div>
                </div>
                <div class="ap-percent-input">
                  <el-input-number
                    v-model="preorderShare"
                    :min="preorderMinShare"
                    :max="preorderRemaining"
                    :precision="2"
                    :step="1"
                    :controls="false"
                  />
                  <span class="ap-percent-unit">%</span>
                </div>
              </div>
            </div>

            <div class="ap-summary">
              <span class="ap-summary-item">
                <span class="ap-summary-label">{{
                  $t("activity.originAmount")
                }}</span>
                <span class="ap-summary-value"
                  >${{ preorderOrigin.toFixed(2) }}</span
                >
              </span>
              <span
                class="ap-summary-item discount"
                v-if="preorderDiscount > 0"
              >
                <span class="ap-summary-label">{{
                  $t("activity.discountAmount")
                }}</span>
                <span class="ap-summary-value"
                  >-${{ preorderDiscount.toFixed(2) }}</span
                >
              </span>
            </div>
          </div>

          <div
            v-if="activityBottomHtml"
            class="ap-copy ap-copy-bottom notice-rich"
            v-html="activityBottomHtml"
          ></div>
        </div>
      </div>

      <div slot="footer" class="notice-dialog-footer">
        <div class="notice-signature">
          <img
            src="../assets/logo.png"
            alt="aetrivol"
            class="notice-signature-logo"
          />
          <span class="notice-signature-name">aetrivol</span>
        </div>
        <div class="ap-footer-actions">
          <div class="ap-footer-total">
            <span>{{ $t("activity.payTotal") }}</span>
            <strong>${{ preorderTotal.toFixed(2) }}</strong>
          </div>
          <el-button class="ap-cancel-btn" @click="preorderVisible = false">
            {{ $t("activity.cancel") }}
          </el-button>
          <el-button
            type="primary"
            class="notice-close-btn"
            :disabled="!activityCanBuy"
            @click="openTradePassword"
          >
            {{ $t("activity.payNow") }}
          </el-button>
        </div>
      </div>
    </el-dialog>

    <TradePasswordModal
      :visible.sync="tradePasswordVisible"
      :z-index="4100"
      :product-info="tradePasswordProduct"
      :loading="tradePasswordLoading"
      :error="tradePasswordError"
      :title="$t('activity.tradeTitle')"
      :label-text="$t('activity.tradeLabel')"
      :placeholder="$t('activity.tradeLabel')"
      :confirm-text="$t('activity.payNow')"
      :cancel-text="$t('activity.cancel')"
      @confirm="handlePreorderPay"
      @cancel="tradePasswordVisible = false"
      @close="tradePasswordVisible = false"
    />
  </div>
</template>

<script>
import consts from "@/utils/consts";
import { loadProductsLanguage } from "@/locales";
import TradePasswordModal from "@/components/toast/TradePasswordModal.vue";
import { noticeHtml, noticeText } from "@/utils/fun";

const PAGE_SIZE = 10;
// 未读数兜底轮询（页面切到后台时暂停）
const POLL_INTERVAL = 30000;

export default {
  name: "NoticeBell",
  components: {
    TradePasswordModal,
  },
  data() {
    return {
      // 下拉控制
      isNoticeDropdownOpen: false,
      // 未读数
      unreadCount: 0,
      // 消息列表
      list: [],
      total: 0,
      page: 1,
      loading: false,
      hasMore: false,
      // 详情弹框
      detailVisible: false,
      activeNotice: null,
      // 轮询
      pollTimer: null,
      lastFetchTime: 0,
      // 列表请求序号，避免切换语言时旧请求覆盖新语言结果
      fetchSeq: 0,
      // 预购活动
      activityItem: null,
      // 打开预购活动时对应的站内消息（展示在预购弹框顶部）
      activityNotice: null,
      activityPasswordVisible: false,
      activityPassword: "",
      activityPasswordError: "",
      activityPasswordChecking: false,
      activityUnlockedPassword: "",
      preorderVisible: false,
      preorderShare: 1,
      tradePasswordVisible: false,
      tradePasswordLoading: false,
      tradePasswordError: "",
    };
  },
  computed: {
    // 消息正文由后台富文本编辑器维护，统一按 HTML 渲染
    detailHtml() {
      return noticeHtml(this.activeNotice && this.activeNotice.content);
    },
    activityTopHtml() {
      return noticeHtml(this.activityNotice && this.activityNotice.content);
    },
    activityBottomHtml() {
      return noticeHtml(
        this.activityNotice && this.activityNotice.bottom_content,
      );
    },
    activityServer() {
      return (this.activityItem && this.activityItem.server) || {};
    },
    preorderServerName() {
      const code =
        this.activityServer.code ||
        (this.activityItem && this.activityItem.server_code) ||
        "";
      // 优先使用产品语言包中的算力名称，后台 title 仅作兜底
      if (code) {
        const key = code + ".s_model";
        const label = this.$t(key);
        if (label && label !== key) return label;
      }
      return (
        this.activityServer.title ||
        this.activityServer.server_title ||
        code ||
        (this.activityNotice && this.activityNotice.title) ||
        "-"
      );
    },
    preorderDiscounts() {
      return (this.activityItem && this.activityItem.discounts) || [];
    },
    preorderRemaining() {
      const server = this.activityServer;
      const value =
        (Number(server.surplus || 0) - Number(server.sale || 0)) * 100;
      return Math.max(0, Number(value.toFixed(2)));
    },
    preorderMinShare() {
      const min = Number(this.activityServer.buy_min || 0) * 100;
      if (min <= 0) return 0.01;
      return Number(min.toFixed(2));
    },
    preorderOrigin() {
      const value = Number(this.activityServer.value || 0);
      return Number(
        ((value * Number(this.preorderShare || 0)) / 100).toFixed(2),
      );
    },
    preorderActiveDiscountIndex() {
      const origin = this.preorderOrigin;
      let activeIndex = -1;
      let activeRate = 0;
      this.preorderDiscounts.forEach((rule, index) => {
        const rate = Number(rule.discount) || 0;
        const condition = Number(rule.condition) || 0;
        if (origin >= condition && rate > activeRate) {
          activeIndex = index;
          activeRate = rate;
        }
      });
      return activeIndex;
    },
    preorderRate() {
      const active = this.preorderDiscounts[this.preorderActiveDiscountIndex];
      return active ? Number(active.discount) || 0 : 0;
    },
    preorderDiscount() {
      return Number((this.preorderOrigin * this.preorderRate).toFixed(2));
    },
    preorderTotal() {
      // 租赁预购活动不收取服务费，支付总额为优惠后金额
      return Number((this.preorderOrigin - this.preorderDiscount).toFixed(2));
    },
    activityCanBuy() {
      return !!(
        this.activityItem &&
        this.activityItem.can_buy &&
        this.preorderRemaining > 0
      );
    },
    tradePasswordProduct() {
      return {
        img: this.activityServer.country_img || "",
        name: this.preorderServerName,
        price: this.preorderTotal.toFixed(2),
      };
    },
  },
  watch: {
    // 切换语言后重新拉取当前语言的标题/正文；详情打开时同步更新
    "$i18n.locale"(newLang) {
      this.lastFetchTime = 0;
      // 预购服务器算力名称来自产品语言包，切换语言后重新加载
      if (this.activityItem && Number(this.activityItem.type) === 1) {
        this.loadServerTranslations();
      }
      if (
        this.isNoticeDropdownOpen ||
        this.detailVisible ||
        this.preorderVisible
      ) {
        this.fetchList(1, newLang);
      }
    },
  },
  created() {
    this.refreshUnreadCount();
  },
  mounted() {
    this.pollTimer = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      this.refreshUnreadCount();
    }, POLL_INTERVAL);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },
  methods: {
    hasToken() {
      return !!(
        localStorage.getItem(consts.KEY_TOKEN) ||
        sessionStorage.getItem(consts.KEY_TOKEN)
      );
    },
    onVisibilityChange() {
      if (!document.hidden) this.refreshUnreadCount();
    },
    // 未读数
    async refreshUnreadCount() {
      if (!this.hasToken()) return;
      try {
        const res = await this.$api.notice.getUnreadCount();
        if (res.code === 0) {
          this.unreadCount = Number(res.data && res.data.count) || 0;
        }
      } catch (error) {
        console.error("获取站内消息未读数失败:", error);
      }
    },
    // 打开下拉（首次或间隔超过 5 秒时刷新第一页）
    openNoticeDropdown() {
      this.isNoticeDropdownOpen = true;
      const now = Date.now();
      if (!this.list.length || now - this.lastFetchTime > 5000) {
        this.fetchList(1);
      }
    },
    closeNoticeDropdown() {
      this.isNoticeDropdownOpen = false;
    },
    // 获取列表（lang 为空时使用当前官网语言）
    async fetchList(page, lang) {
      if (!this.hasToken()) return;
      const seq = ++this.fetchSeq;
      this.loading = true;
      this.lastFetchTime = Date.now();
      try {
        const res = await this.$api.notice.getList({
          page,
          size: PAGE_SIZE,
          lang: lang || this.$i18n.locale,
        });
        if (seq !== this.fetchSeq) return;
        if (res.code === 0) {
          const data = res.data || {};
          const items = data.data || [];
          this.list = page === 1 ? items : this.list.concat(items);
          this.total = Number(data.total) || 0;
          this.page = Number(data.current) || page;
          this.hasMore = this.list.length < this.total;
          // 详情弹窗打开时，切换语言后同步为当前语言的内容
          if (page === 1 && this.detailVisible && this.activeNotice) {
            const current = items.find(
              (item) => item.id === this.activeNotice.id,
            );
            if (current) {
              this.activeNotice = current;
            } else {
              this.detailVisible = false;
            }
          }
          // 活动弹框打开时，切换语言后同步顶部站内消息文案
          if (page === 1 && this.activityNotice) {
            const current = items.find(
              (item) => item.id === this.activityNotice.id,
            );
            if (current) this.activityNotice = current;
          }
          if (page === 1) this.refreshUnreadCount();
        }
      } catch (error) {
        if (seq !== this.fetchSeq) return;
        console.error("获取站内消息失败:", error);
        if (page === 1) {
          this.list = [];
          this.total = 0;
          this.hasMore = false;
        }
      } finally {
        if (seq === this.fetchSeq) this.loading = false;
      }
    },
    loadMore() {
      if (this.loading || !this.hasMore) return;
      this.fetchList(this.page + 1);
    },
    // 打开详情并标记已读
    async showDetail(item) {
      this.activeNotice = item;
      this.detailVisible = true;
      if (item.is_read === 2) return;

      item.is_read = 2;
      if (this.unreadCount > 0) this.unreadCount -= 1;
      try {
        const res = await this.$api.notice.read({ id: item.id });
        if (res.code !== 0) {
          console.error("标记站内消息已读失败:", res.msg);
          this.refreshUnreadCount();
        }
      } catch (error) {
        console.error("标记站内消息已读失败:", error);
        this.refreshUnreadCount();
      }
    },
    async handleNoticeClick(item) {
      if (item.type === 1 && item.activity_id) {
        await this.markNoticeRead(item);
        await this.openActivity(item);
        return;
      }
      this.showDetail(item);
    },
    async markNoticeRead(item) {
      if (item.is_read === 2) return;
      item.is_read = 2;
      if (this.unreadCount > 0) this.unreadCount -= 1;
      try {
        const res = await this.$api.notice.read({ id: item.id });
        if (res.code !== 0) {
          console.error("mark notice read failed:", res.msg);
          this.refreshUnreadCount();
        }
      } catch (error) {
        console.error("mark notice read failed:", error);
        this.refreshUnreadCount();
      }
    },
    async openActivity(item) {
      this.isNoticeDropdownOpen = false;
      this.detailVisible = false;
      try {
        const res = await this.$api.activity.getInfo({
          id: item.activity_id,
          // 预购活动中的服务器等文案需按当前官网语言返回
          lang: this.$i18n.locale,
        });
        if (res.code !== 0) {
          this.$message?.warning(res.msg || this.$t("activity.loadFail"));
          return;
        }
        this.activityNotice = item;
        this.activityItem = res.data;
        this.loadServerTranslations();
        this.activityUnlockedPassword = "";
        this.activityPassword = "";
        this.activityPasswordError = "";
        if (this.activityItem.type === 1) {
          if (this.activityItem.need_password === 1) {
            this.activityPasswordVisible = true;
          } else {
            this.openPreorder();
          }
        }
      } catch (error) {
        console.error("load activity failed:", error);
        this.$message?.error(this.$t("activity.loadFail"));
      }
    },
    loadServerTranslations() {
      if (!this.activityItem || Number(this.activityItem.type) !== 1) return;
      const code =
        this.activityServer.code ||
        (this.activityItem && this.activityItem.server_code) ||
        "";
      if (!code) return;
      loadProductsLanguage([code], { path: "server/" });
    },
    async verifyActivityPassword() {
      const password = (this.activityPassword || "").trim();
      if (!password) {
        this.activityPasswordError = this.$t("activity.passwordRequired");
        return;
      }
      if (!this.activityItem) return;
      this.activityPasswordChecking = true;
      this.activityPasswordError = "";
      try {
        const res = await this.$api.activity.verifyPassword({
          id: this.activityItem.id,
          password,
        });
        if (res.code !== 0) {
          this.activityPasswordError =
            res.msg || this.$t("activity.passwordError");
          return;
        }
        this.activityUnlockedPassword = password;
        this.activityPasswordVisible = false;
        this.openPreorder();
      } catch (error) {
        console.error("verify activity password failed:", error);
        this.activityPasswordError = this.$t("activity.passwordError");
      } finally {
        this.activityPasswordChecking = false;
      }
    },
    openPreorder() {
      const min = this.preorderMinShare;
      this.preorderShare = Math.min(min, this.preorderRemaining || min);
      if (!this.preorderShare || this.preorderShare <= 0) {
        this.preorderShare = this.preorderRemaining || 1;
      }
      this.preorderVisible = true;
    },
    formatShare(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return "0";
      return String(Number(num.toFixed(2)));
    },
    openTradePassword() {
      if (!this.activityCanBuy) {
        this.$message?.warning(this.$t("activity.cannotBuy"));
        return;
      }
      if (Number(this.preorderShare) < this.preorderMinShare) {
        this.$message?.warning(
          this.$t("activity.minShare", { percent: this.preorderMinShare }),
        );
        return;
      }
      this.tradePasswordError = "";
      this.tradePasswordVisible = true;
    },
    async handlePreorderPay(password) {
      if (!this.activityItem) return;
      this.tradePasswordLoading = true;
      this.tradePasswordError = "";
      try {
        const res = await this.$api.activity.preorder({
          id: this.activityItem.id,
          share: Number(this.preorderShare),
          activity_password: this.activityUnlockedPassword,
          trade_password: password,
        });
        if (res.code !== 0) {
          this.tradePasswordError = res.msg || this.$t("activity.payFail");
          return;
        }
        this.$message?.success(this.$t("activity.paySuccess"));
        this.tradePasswordVisible = false;
        this.preorderVisible = false;
      } catch (error) {
        console.error("activity preorder failed:", error);
        this.tradePasswordError = this.$t("activity.payFail");
      } finally {
        this.tradePasswordLoading = false;
      }
    },
    onDetailClosed() {
      this.activeNotice = null;
    },
    preview(content) {
      // 列表只展示纯文本摘要，去掉后台富文本的标签
      const text = noticeText(content);
      return text.length > 60 ? text.slice(0, 60) + "..." : text;
    },
    formatTime(item) {
      return item.publish_time || item.create_time || "";
    },
  },
};
</script>

<style scoped>
/* ===== 铃铛触发器 ===== */
.notice-dropdown {
  position: relative;
  cursor: pointer;
}

.notice-trigger {
  position: relative;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.3s ease;
}

.notice-trigger:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.notice-dot {
  position: absolute;
  top: 13px;
  right: 6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff4d4f;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.9);
}

/* ===== 下拉面板 ===== */
.notice-panel {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  width: 400px;
  max-width: 92vw;
  background: rgba(20, 20, 35, 0.97);
  backdrop-filter: blur(24px);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transform-origin: top right;
  overflow: hidden;
  z-index: 1002;
}

/* 悬浮桥接，避免鼠标穿过 12px 间隙时面板关闭 */
.notice-panel::before {
  content: "";
  position: absolute;
  top: -12px;
  left: 0;
  right: 0;
  height: 12px;
}

.notice-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.notice-panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
}

.notice-panel-unread {
  font-size: 12px;
  color: #00b4ff;
}

/* ===== 消息列表 ===== */
.notice-list {
  max-height: 430px;
  overflow-y: auto;
}

.notice-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: background 0.15s ease;
}

.notice-item:last-child {
  border-bottom: none;
}

.notice-item:hover {
  background: rgba(0, 180, 255, 0.1);
}

.notice-item-dot {
  width: 6px;
  height: 6px;
  margin-top: 7px;
  border-radius: 50%;
  background: #ff4d4f;
  flex-shrink: 0;
}

.notice-item-content {
  flex: 1;
  min-width: 0;
}

.notice-item-title {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notice-item.unread .notice-item-title {
  color: #fff;
  font-weight: 600;
}

.notice-item-preview {
  margin-top: 3px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notice-item-time {
  margin-top: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
}

/* ===== 空状态 / 底部按钮 ===== */
.notice-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 42px 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
}

.notice-empty-icon {
  font-size: 24px;
  opacity: 0.6;
}

.notice-panel-footer {
  padding: 8px 12px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.notice-more-btn {
  width: 100%;
  padding: 9px 0;
  border-radius: 10px;
  border: 1px solid rgba(0, 180, 255, 0.25);
  background: rgba(0, 180, 255, 0.1);
  color: #00b4ff;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
}

.notice-more-btn:hover:not(:disabled) {
  background: rgba(0, 180, 255, 0.18);
  color: #fff;
}

.notice-more-btn:disabled {
  cursor: default;
  opacity: 0.45;
}

/* 滚动条美化 */
.notice-list::-webkit-scrollbar {
  width: 4px;
}
.notice-list::-webkit-scrollbar-track {
  background: transparent;
}
.notice-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}

/* ===== 过渡动画 ===== */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.96) translateY(-8px);
}

/* ===== 消息详情弹框 ===== */
/* 弹框层级通过 el-dialog 的 z-index 控制，保证遮罩始终低于弹框内容 */

.notice-dialog >>> .el-dialog {
  position: relative;
  overflow: hidden;
  background: radial-gradient(
      120% 90% at 100% 0%,
      rgba(0, 180, 255, 0.14),
      transparent 58%
    ),
    linear-gradient(180deg, #181c2e 0%, #11141f 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  box-shadow: 0 32px 90px rgba(0, 0, 0, 0.65),
    0 0 0 1px rgba(0, 180, 255, 0.04) inset;
  max-width: 92vw;
}

/* 顶部渐变光带 */
.notice-dialog >>> .el-dialog::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(
    90deg,
    transparent,
    #00b4ff 28%,
    #4f7cff 72%,
    transparent
  );
  opacity: 0.9;
  pointer-events: none;
}

.notice-dialog >>> .el-dialog__header {
  padding: 28px 32px 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.notice-dialog >>> .el-dialog__headerbtn {
  top: 26px;
  right: 28px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
  transition: background 0.2s ease;
}

.notice-dialog >>> .el-dialog__headerbtn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.notice-dialog >>> .el-dialog__headerbtn .el-dialog__close {
  color: rgba(255, 255, 255, 0.45);
  font-size: 18px;
  transition: color 0.2s ease;
}

.notice-dialog >>> .el-dialog__headerbtn:hover .el-dialog__close {
  color: #fff;
}

.notice-dialog >>> .el-dialog__body {
  padding: 26px 32px 10px;
}

.notice-dialog >>> .el-dialog__footer {
  padding: 20px 32px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* 弹框头部 */
.notice-dialog-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-right: 44px;
}

.notice-dialog-icon {
  width: 50px;
  height: 50px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
  color: #00b4ff;
  background: linear-gradient(
    135deg,
    rgba(0, 180, 255, 0.22),
    rgba(79, 124, 255, 0.1)
  );
  border: 1px solid rgba(0, 180, 255, 0.24);
  box-shadow: 0 8px 24px rgba(0, 180, 255, 0.12);
}

.notice-dialog-heading {
  flex: 1;
  min-width: 0;
}

.notice-dialog-kicker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: rgba(0, 180, 255, 0.9);
}

.notice-dialog-kicker::before {
  content: "";
  width: 16px;
  height: 2px;
  border-radius: 2px;
  background: linear-gradient(90deg, #00b4ff, rgba(0, 180, 255, 0.2));
}

.notice-dialog-headline {
  margin: 8px 0 0;
  color: #fff;
  font-size: 21px;
  font-weight: 600;
  line-height: 1.45;
  letter-spacing: 0.2px;
  word-break: break-word;
}

/* 弹框正文 */
.notice-detail {
  padding-top: 2px;
}

.notice-detail-meta {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 16px;
  font-size: 12px;
  letter-spacing: 0.3px;
  color: rgba(255, 255, 255, 0.36);
}

.notice-detail-meta svg {
  color: rgba(0, 180, 255, 0.7);
}

.notice-detail-content {
  box-sizing: border-box;
  max-height: 52vh;
  overflow-y: auto;
  padding: 22px 24px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.025);
  color: rgba(255, 255, 255, 0.86);
  font-size: 15px;
  line-height: 1.9;
  word-break: break-word;
}

.notice-detail-content::-webkit-scrollbar {
  width: 5px;
}
.notice-detail-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 5px;
}

/* 弹框底部：左侧官方落款 */
.notice-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.notice-signature {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.notice-signature-logo {
  display: block;
  width: auto;
  height: 28px;
  filter: drop-shadow(0 0 10px rgba(0, 180, 255, 0.3));
}

.notice-signature-name {
  color: rgba(255, 255, 255, 0.85);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.4px;
}

.notice-close-btn.el-button {
  min-width: 104px;
  height: 40px;
  padding: 0 26px;
  border: 1px solid rgba(0, 180, 255, 0.35);
  border-radius: 10px;
  background: linear-gradient(135deg, #00a6f0 0%, #3f6dff 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 1px;
  box-shadow: 0 8px 24px rgba(0, 140, 255, 0.28);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.notice-close-btn.el-button:hover,
.notice-close-btn.el-button:focus {
  border-color: rgba(0, 180, 255, 0.5);
  background: linear-gradient(135deg, #12b5ff 0%, #4f7cff 100%);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 12px 30px rgba(0, 140, 255, 0.36);
}

.notice-close-btn.el-button:active {
  transform: translateY(0);
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .notice-panel {
    width: 92vw;
    right: -60px;
  }
  .notice-trigger {
    width: 30px;
    height: 30px;
  }

  .notice-dialog >>> .el-dialog {
    border-radius: 16px;
  }
  .notice-dialog >>> .el-dialog__header {
    padding: 22px 20px 18px;
  }
  .notice-dialog >>> .el-dialog__headerbtn {
    top: 20px;
    right: 16px;
  }
  .notice-dialog >>> .el-dialog__body {
    padding: 20px 20px 8px;
  }
  .notice-dialog >>> .el-dialog__footer {
    padding: 16px 20px 20px;
  }
  .notice-dialog-header {
    gap: 12px;
    padding-right: 34px;
  }
  .notice-dialog-icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
  }
  .notice-dialog-headline {
    font-size: 18px;
  }
  .notice-detail-content {
    max-height: 46vh;
    padding: 18px;
    font-size: 14px;
  }
  .notice-signature-logo {
    height: 24px;
  }
  .notice-signature-name {
    font-size: 14px;
  }
  .notice-close-btn.el-button {
    min-width: 88px;
    height: 38px;
    padding: 0 18px;
  }
}
/* ===== 预购活动弹框 ===== */
/* 预购弹框复用消息弹框样式，层级由 :z-index="4000" 控制 */
.activity-dialog-title {
  color: #eaf6ff;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 0.5px;
}
.activity-password-body {
  color: rgba(255, 255, 255, 0.78);
}
.activity-password-tip {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.56);
}
.activity-password-error {
  margin-top: 10px;
  color: #ff6b6b;
  font-size: 13px;
}
.activity-preorder {
  color: rgba(255, 255, 255, 0.82);
}
/* 预购弹框：文案与预购功能共用一个内容面板，上方文案 → 预购操作 → 下方文案 */
.ap-body {
  box-sizing: border-box;
  max-height: 58vh;
  overflow-y: auto;
  padding: 22px 24px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.025);
}
.ap-body::-webkit-scrollbar {
  width: 5px;
}
.ap-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 5px;
}
.ap-copy {
  color: rgba(255, 255, 255, 0.86);
  font-size: 15px;
  line-height: 1.9;
  word-break: break-word;
}
.ap-copy p {
  margin: 0 0 8px;
  white-space: pre-wrap;
}
.ap-copy p:last-child {
  margin-bottom: 0;
}

/* ===== 富文本正文（后台富文本编辑器输出的 HTML） ===== */
.notice-rich {
  word-break: break-word;
}
.notice-rich ::v-deep p {
  margin: 0 0 8px;
}
.notice-rich ::v-deep p:last-child {
  margin-bottom: 0;
}
.notice-rich ::v-deep ul,
.notice-rich ::v-deep ol {
  margin: 8px 0;
  padding-left: 22px;
}
.notice-rich ::v-deep ul {
  list-style: disc;
}
.notice-rich ::v-deep ol {
  list-style: decimal;
}
.notice-rich ::v-deep li {
  margin: 4px 0;
}
.notice-rich ::v-deep h1,
.notice-rich ::v-deep h2,
.notice-rich ::v-deep h3,
.notice-rich ::v-deep h4,
.notice-rich ::v-deep h5,
.notice-rich ::v-deep h6 {
  margin: 14px 0 8px;
  color: #fff;
  font-weight: 600;
  line-height: 1.5;
}
.notice-rich ::v-deep h1 {
  font-size: 22px;
}
.notice-rich ::v-deep h2 {
  font-size: 20px;
}
.notice-rich ::v-deep h3 {
  font-size: 18px;
}
.notice-rich ::v-deep h4,
.notice-rich ::v-deep h5,
.notice-rich ::v-deep h6 {
  font-size: 16px;
}
.notice-rich ::v-deep a {
  color: #00b4ff;
  text-decoration: underline;
  word-break: break-all;
}
.notice-rich ::v-deep img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}
.notice-rich ::v-deep hr {
  margin: 14px 0;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}
.notice-rich ::v-deep blockquote {
  margin: 10px 0;
  padding: 8px 14px;
  border-left: 3px solid rgba(0, 180, 255, 0.45);
  background: rgba(255, 255, 255, 0.03);
  color: rgba(255, 255, 255, 0.72);
}
.notice-rich ::v-deep code {
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  font-family: Consolas, Menlo, monospace;
  font-size: 0.92em;
}
.notice-rich ::v-deep pre {
  margin: 10px 0;
  padding: 12px;
  overflow-x: auto;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
}
.notice-rich ::v-deep pre code {
  padding: 0;
  background: none;
}
/* 表格边框兜底：单元格底色由后台内联 style 决定，不在这里覆盖 */
.notice-rich ::v-deep table {
  width: 100%;
  margin: 12px 0;
  border-collapse: collapse;
  font-size: 13px;
}
.notice-rich ::v-deep th,
.notice-rich ::v-deep td {
  padding: 8px 10px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  text-align: left;
  vertical-align: top;
  word-break: break-word;
}
.notice-rich ::v-deep th {
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-weight: 600;
}
.ap-copy + .ap-panel,
.ap-panel + .ap-copy {
  /* margin-top: 20px; */
  padding-top: 20px;
  /*border-top: 1px solid rgba(255, 255, 255, 0.08);*/
}
.ap-panel {
  background: linear-gradient(
    135deg,
    rgba(0, 180, 255, 0.09),
    rgba(0, 102, 255, 0.03)
  );
  padding: 20px;
  border-radius: 20px;
  margin-top: 15px;
}
.ap-panel {
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(0, 0.9fr);
  align-items: start;
  gap: 12px 16px;
  min-width: 0;
}
.ap-panel > .ap-discounts {
  grid-column: 1 / -1;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.ap-panel > .ap-summary {
  align-self: center;
}
.ap-panel > .ap-discounts,
.ap-panel > .ap-form,
.ap-panel > .ap-summary {
  margin-top: 0;
}
.ap-section-title {
  margin-bottom: 5px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 12px;
  font-weight: 600;
}
.ap-discounts,
.ap-form {
  margin-top: 12px;
}
.ap-panel > .ap-discounts:first-child,
.ap-panel > .ap-form:first-child {
  margin-top: 0;
}
.ap-discount-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 6px 8px;
}
.ap-discount-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
  padding: 6px 9px;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
}
.ap-discount-text {
  flex: 1;
  min-width: 0;
  line-height: 1.4;
}
.ap-discount-item.active {
  border-color: rgba(82, 196, 26, 0.6);
  background: rgba(82, 196, 26, 0.12);
  color: #95de64;
}
.ap-share-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.ap-share-head .ap-section-title {
  margin-bottom: 0;
}
.ap-share-remaining {
  flex: none;
  padding: 2px 8px;
  /* border: 1px solid rgba(255, 193, 7, 0.2); */
  /* border-radius: 7px; */
  /* background: rgba(255, 193, 7, 0.12); */
  /* color: #ffc107; */
  font-size: 11px;
  font-weight: 600;
}
.ap-share-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
}
.ap-share-slider-wrap {
  flex: 1;
  min-width: 0;
}
.ap-share-slider {
  padding: 0 4px;
}
.ap-share-slider >>> .el-slider__runway {
  height: 6px;
  margin: 8px 0;
  background: rgba(255, 255, 255, 0.12);
}
.ap-share-slider >>> .el-slider__bar {
  height: 6px;
  background: #00b4ff;
}
.ap-share-slider >>> .el-slider__button {
  width: 16px;
  height: 16px;
  border: 2px solid #00b4ff;
  background: #0f1a2b;
  box-shadow: 0 0 0 3px rgba(0, 180, 255, 0.12);
}
.ap-slider-labels {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.3);
  font-size: 11px;
  line-height: 1;
}
.ap-slider-current {
  color: #00b4ff;
  font-weight: 700;
}
.ap-percent-input {
  position: relative;
  display: flex;
  flex: none;
  align-items: center;
  width: 88px;
  height: 32px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  transition: border-color 0.2s, box-shadow 0.2s;
}
.ap-percent-input:focus-within {
  border-color: rgba(0, 180, 255, 0.6);
  box-shadow: 0 0 0 2px rgba(0, 180, 255, 0.1);
}
.ap-percent-input >>> .el-input-number {
  width: 100%;
}
.ap-percent-input >>> .el-input__inner {
  height: 30px;
  padding: 0 20px 0 8px;
  border: 0;
  background: transparent;
  color: #eaf6ff;
  font-size: 13px;
  text-align: center;
}
.ap-percent-unit {
  position: absolute;
  right: 8px;
  color: rgba(255, 255, 255, 0.45);
  font-size: 12px;
  pointer-events: none;
}
.ap-summary {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px 14px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
}
.ap-summary-item {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  color: rgba(255, 255, 255, 0.52);
  font-size: 12px;
  white-space: nowrap;
}
.ap-summary-value {
  color: #eaf6ff;
  font-size: 13px;
  font-weight: 600;
}
.ap-summary-item.discount,
.ap-summary-item.discount .ap-summary-value {
  color: #00d4a0;
}
.ap-footer-total {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-right: 4px;
}
.ap-footer-total span {
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
}
.ap-footer-total strong {
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
}
.ap-footer-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}
.ap-footer-actions >>> .el-button + .el-button {
  margin-left: 0;
}
.ap-cancel-btn.el-button {
  height: 40px;
  padding: 0 22px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.72);
}
.ap-cancel-btn.el-button:hover,
.ap-cancel-btn.el-button:focus {
  border-color: rgba(0, 180, 255, 0.32);
  background: rgba(0, 180, 255, 0.08);
  color: #fff;
}
.notice-close-btn.el-button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
  transform: none;
  box-shadow: none;
}
.activity-password-dialog >>> .el-dialog {
  max-width: 94vw;
  background: #0e1526;
  border: 1px solid rgba(0, 180, 255, 0.18);
  border-radius: 18px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
}
.activity-password-dialog >>> .el-dialog__title,
.activity-password-dialog >>> .el-dialog__headerbtn .el-dialog__close {
  color: rgba(255, 255, 255, 0.7);
}
.activity-password-dialog >>> .el-dialog__body {
  color: rgba(255, 255, 255, 0.75);
}
@media (max-width: 900px) {
  .ap-panel {
    grid-template-columns: minmax(0, 1fr);
    gap: 14px;
  }
  .ap-panel > .ap-discounts {
    padding-bottom: 0;
    border-bottom: 0;
  }
  .ap-panel > .ap-form,
  .ap-panel > .ap-summary {
    align-self: stretch;
    padding-top: 14px;
    padding-left: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    border-left: 0;
  }
}
@media (max-width: 768px) {
  .ap-body {
    max-height: 54vh;
    padding: 18px;
  }
  .ap-copy {
    font-size: 14px;
    line-height: 1.8;
  }
  .ap-share-row {
    gap: 10px;
  }
  .ap-percent-input {
    width: 84px;
  }
  .ap-summary {
    align-items: flex-start;
  }
  .activity-preorder-dialog .notice-dialog-footer {
    flex-wrap: wrap;
    gap: 12px;
  }
  .ap-footer-actions {
    width: 100%;
    justify-content: flex-end;
  }
  .ap-footer-total {
    width: 100%;
    justify-content: flex-end;
  }
  .activity-password-dialog >>> .el-dialog {
    width: 92% !important;
  }
}
</style>
